import { useEffect } from 'react';
import { fabric } from 'fabric';
import { buildSelectionStyle } from '../utils/loadImageToCanvas';
import { colorToRgba } from '../utils/colorUtils';
import useDetectionStore from '../store/useDetectionStore';

const useBoundingBoxDrawing = (
    canvas,
    mode,
    rect,
    startX,
    startY,
    currentClassId,
    zoomLevel,
    getCurrentImage,
    updateAnnotations,
    setStartX,
    setStartY,
    setRect,
    classes,
    BASE_STROKE_WIDTH,
    isCrosshairEnabled,
    saveState
) => {
    useEffect(() => {
        if (!canvas || mode !== 'draw') return;

        const selectedClass = classes.find(cls => cls.id === currentClassId);

        let verticalLine = null;
        let horizontalLine = null;

        const drawCrosshair = (pointer) => {
            // Remove previous lines
            if (verticalLine) canvas.remove(verticalLine);
            if (horizontalLine) canvas.remove(horizontalLine);

            const currentImage = getCurrentImage();
            if (!currentImage) return;

            // White dashed line over a dark shadow: the guide sits on top of
            // arbitrary photography, so it needs its own contrast rather than a
            // theme color that can vanish into the image.
            const makeGuide = (points) => new fabric.Line(points, {
                stroke: 'rgba(255, 255, 255, 0.95)',
                selectable: false,
                evented: false,
                strokeWidth: 1 / zoomLevel,
                strokeDashArray: [6 / zoomLevel, 4 / zoomLevel],
                shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.9)', blur: 2, offsetX: 0, offsetY: 0 }),
                excludeFromExport: true,
                isGuide: true,
            });

            verticalLine = makeGuide([pointer.x, 0, pointer.x, currentImage.originalHeight]);
            horizontalLine = makeGuide([0, pointer.y, currentImage.originalWidth, pointer.y]);

            canvas.add(verticalLine);
            canvas.add(horizontalLine);
            verticalLine.moveTo(9999); // make sure it's on top
            horizontalLine.moveTo(9999);
        };

        const removeCrosshair = () => {
            if (verticalLine) canvas.remove(verticalLine);
            if (horizontalLine) canvas.remove(horizontalLine);
            verticalLine = null;
            horizontalLine = null;
        };

        const onMouseDown = (o) => {
            if (!selectedClass) {
                // highlight the Classes panel instead of blocking with an alert
                useDetectionStore.getState().flagClassAttention();
                return;
            }
            const currentImage = getCurrentImage();
            if (!currentImage) return;

            saveState?.();

            const pointer = canvas.getPointer(o.e);
            const x = Math.max(0, Math.min(pointer.x, currentImage.originalWidth));
            const y = Math.max(0, Math.min(pointer.y, currentImage.originalHeight));

            setStartX(x);
            setStartY(y);
            const newRect = new fabric.Rect({
                left: x,
                top: y,
                width: 0,
                height: 0,
                fill: useDetectionStore.getState().showFill
                    ? colorToRgba(selectedClass?.color, 0.14)
                    : 'transparent',
                stroke: selectedClass?.color || 'grey',
                strokeWidth: BASE_STROKE_WIDTH / zoomLevel,
                selectable: true,
                ...buildSelectionStyle(),
                classData: selectedClass,
            });
            canvas.add(newRect);
            setRect(newRect);
        };

        const onMouseMove = (o) => {
            if (!rect) return;
            const pointer = canvas.getPointer(o.e);
            if (isCrosshairEnabled) {
                drawCrosshair(pointer);
            } else {
                removeCrosshair();
            }

            const currentImage = getCurrentImage();
            const maxX = currentImage.originalWidth;
            const maxY = currentImage.originalHeight;

            let x = Math.min(Math.max(0, Math.min(pointer.x, startX)), maxX);
            let y = Math.min(Math.max(0, Math.min(pointer.y, startY)), maxY);
            let w = Math.min(Math.abs(pointer.x - startX), maxX - x);
            let h = Math.min(Math.abs(pointer.y - startY), maxY - y);

            rect.set({ left: x, top: y, width: w, height: h });
            canvas.requestRenderAll();
        };

        const onMouseUp = () => {
            removeCrosshair();

            if (!rect) return;
            const currentImage = getCurrentImage();

            // discard accidental click-drags: under ~4 px on screen, not in image px
            // (5 image px is a legitimate box when zoomed into a large photo)
            if (rect.width * zoomLevel < 4 || rect.height * zoomLevel < 4) {
                canvas.remove(rect);
                setRect(null);
                return;
            }

            rect.setCoords();

            const centerX = (rect.left + rect.width / 2) / currentImage.originalWidth;
            const centerY = (rect.top + rect.height / 2) / currentImage.originalHeight;
            const width = rect.width / currentImage.originalWidth;
            const height = rect.height / currentImage.originalHeight;

            const ann = { classId: currentClassId, centerX, centerY, width, height };
            const newAnns = [...currentImage.annotations, ann];
            updateAnnotations(newAnns);
            
            rect.data = { annIndex: newAnns.length - 1, type: 'bbox' };
            rect.classData = selectedClass
            setRect(null);
            canvas.requestRenderAll();
        };

        canvas.on('mouse:down', onMouseDown);
        canvas.on('mouse:move', onMouseMove);
        canvas.on('mouse:up', onMouseUp);

        return () => {
            removeCrosshair();
            canvas.off('mouse:down', onMouseDown);
            canvas.off('mouse:move', onMouseMove);
            canvas.off('mouse:up', onMouseUp);
        };
    }, [
        canvas,
        mode,
        rect,
        startX,
        startY,
        currentClassId,
        zoomLevel,
        getCurrentImage,
        updateAnnotations,
        setStartX,
        setStartY,
        setRect,
        classes,
        BASE_STROKE_WIDTH,
        isCrosshairEnabled,
        saveState
    ]);
};

export default useBoundingBoxDrawing;
